import "./FindingsTab.css";

import {
  ApplicationIcon,
  Box,
  Chip,
  Icon,
  Link,
  Typography,
} from "@saviynt/design-system";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useInventoryAssetFindigsData } from "../../../../hooks/usePolicy";
import LoadErrorWrapper from "../../../../components/LoadErrorWrapper/LoadErrorWrapper";
import { getPoliciesColor } from "../../../PoliciesPageList/PoliciesPageListColumnConfig";
import { useSidecar } from "../../../../hooks/useSidecar";
import { PolicySeverity } from "@repo/api/schemas/policies.schema";
import { getPoliciesIcon } from "../util";
import { AccordionHeader } from "../../../../components/Accordion/AccordionHeader";
import PoliciesSidepanel, {
  getApplicationSourceIcon,
} from "../../../PoliciesSidepanel/PoliciesSidepanel";
import Accordion from "../../../../components/Accordion/Accordion";
import { capitalizeFirstLetter } from "@repo/mfe-core";
import {
  FindingItem,
  FindingsBySeverity,
} from "@repo/api/schemas/policiesInventory.schema";
import { VariantType } from "../InventorySidePanel";

interface FindingsTabProps {
  assetId: string;
  variant: VariantType;
}

export default function FindingsTab({ assetId, variant }: FindingsTabProps) {
  const intl = useIntl();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedRemediation, setExpandedRemediation] = useState<Set<string>>(
    new Set(),
  );
  const { data, isLoading, isError } = useInventoryAssetFindigsData(assetId);

  const { isSidecarOpen, sidecarMetaData, closeSidecar, openAssetSidecar } =
    useSidecar({ id: "", name: "" });

  const findings = useMemo(() => {
    if (!data?.data) {
      return {} as Partial<FindingsBySeverity>;
    }

    return Object.fromEntries(
      Object.entries(data.data).filter(([, value]) => value.length > 0),
    ) as Partial<FindingsBySeverity>;
  }, [data]);

  useEffect(() => {
    const firstSeverity = Object.keys(findings)[0];
    if (firstSeverity) {
      setExpandedItems(new Set([firstSeverity]));
    }
  }, [assetId, findings]);

  const toggleExpanded = (severity: string) => {
    setExpandedItems(
      expandedItems.has(severity) ? new Set() : new Set([severity]),
    );
  };

  const toggleExpandedRemediation = (triggerPolicyId: string) => {
    setExpandedRemediation(
      expandedRemediation.has(triggerPolicyId)
        ? new Set()
        : new Set([triggerPolicyId]),
    );
  };

  if (isLoading || isError) {
    return (
      <Box className="w-full">
        <LoadErrorWrapper isLoading={isLoading} isError={isError} />
      </Box>
    );
  }

  return (
    <>
      {isSidecarOpen && (
        <PoliciesSidepanel
          isOpen={isSidecarOpen}
          closeSidepanel={closeSidecar}
          policyData={sidecarMetaData}
          variant={variant}
          isCloseButtonHidden={true}
        />
      )}
      <Box
        className="Nhi-FindingsTab-Container"
        dataTestId="NHI-FindingsTab-Content"
        role="region"
        ariaLabel={intl.formatMessage({
          defaultMessage: "Findings tab content",
          description: "Findings tab content",
          id: "FRQdLF",
        })}
        tabIndex={0}
      >
        {Object.values(findings).some((arr) => arr.length > 0) ? (
          <div className="m-4 space-y-4">
            {Object.entries(findings).map(([severity, findingsList]) => {
              const isExpanded = expandedItems.has(severity);
              const title = (
                <div className="flex gap-1 items-center justify-center">
                  <Chip
                    kind="subtleFilled"
                    leftIcon={
                      <Icon
                        kind={getPoliciesIcon(severity as PolicySeverity)}
                      />
                    }
                    label={capitalizeFirstLetter(severity)}
                    color={getPoliciesColor(severity as PolicySeverity)}
                    className={severity}
                  />
                  <Typography kind="body2" desktop="body2-d" mobile="body2-d">
                    {intl.formatMessage(
                      {
                        defaultMessage: `{count, plural,one {# Finding}other {# Findings}}`,
                        id: "9CDgZU",
                        description: "Findings count",
                      },
                      { count: findingsList.length },
                    )}
                  </Typography>
                </div>
              );

              return (
                <div
                  className="policies-accordion-container"
                  key={`finding-severity-${severity}`}
                >
                  <AccordionHeader
                    title={title}
                    isExpanded={isExpanded}
                    onToggle={() => toggleExpanded(severity)}
                  />
                  {isExpanded &&
                    findingsList.map(
                      (finding: FindingItem, findingIndex: number) => {
                        const isRemediationOptionsExpanded =
                          expandedRemediation.has(finding.triggerPolicyId);
                        return (
                          <div
                            key={`finding-${findingIndex}`}
                            className="policies-accordion-container mt-4"
                          >
                            <Link
                              text={finding.policyName}
                              kind="button"
                              onClick={() => {
                                openAssetSidecar({
                                  id: finding.triggerPolicyId,
                                  name: finding.policyName,
                                });
                              }}
                            />
                            {(finding?.remediationOptions?.length ?? 0) > 0 && (
                              <AccordionHeader
                                title={
                                  <div className="flex items-center gap-2">
                                    <Icon
                                      kind="Task"
                                      color="--color-foreground-teal-subtle"
                                    />
                                    <Typography
                                      kind="body3"
                                      desktop="body3-d"
                                      mobile="body3-d"
                                      color="--color-foreground-neutral-subtle"
                                    >
                                      {intl.formatMessage(
                                        {
                                          defaultMessage:
                                            "Remediation Options ({count})",
                                          id: "ogSeA+",
                                          description: "Remediation title",
                                        },
                                        {
                                          count:
                                            finding.remediationOptions
                                              ?.length ?? 0,
                                        },
                                      )}
                                    </Typography>
                                  </div>
                                }
                                isExpanded={isRemediationOptionsExpanded}
                                onToggle={() =>
                                  toggleExpandedRemediation(
                                    finding.triggerPolicyId,
                                  )
                                }
                              />
                            )}
                            {isRemediationOptionsExpanded && (
                              <Box className="space-y-2">
                                {finding?.remediationOptions?.map((option) => (
                                  <Box className="space-y-2">
                                    <Box className="nhi-accordion-header-leading-control-area">
                                      <ApplicationIcon
                                        logo={getApplicationSourceIcon(
                                          option.source,
                                        )}
                                        size="xSmall"
                                        alt={intl.formatMessage(
                                          {
                                            defaultMessage:
                                              "Applicaton icon for {source}",
                                            id: "aS0NML",
                                            description: "Application icon",
                                          },
                                          { source: option.source },
                                        )}
                                      />
                                      <Typography
                                        kind="body3"
                                        desktop="body3-d"
                                        mobile="body3-d"
                                        color="--color-foreground-neutral-subtle"
                                        className="nhi-accordion-header-text-title"
                                      >
                                        {option.displayName}
                                      </Typography>
                                    </Box>
                                    <Box className="space-y-2">
                                      <Accordion
                                        key={option.source}
                                        options={option.options}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </div>
                        );
                      },
                    )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className="NHI-FindingsTab-noFindings"
            role="status"
            aria-live="polite"
          >
            <Icon
              kind="Party"
              color="--color-foreground-primary-subtle"
              size="large"
            />
            <Typography kind="body1">
              {intl.formatMessage({
                defaultMessage: "No Findings, way to go!",
                description: "No Findings, way to go!",
                id: "mDYtwH",
              })}
            </Typography>
          </div>
        )}
      </Box>
    </>
  );
}
